import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, CardLayout, InputField, SelectField } from "../v3";
import { AdminService, CompanyService } from "../../services";
import { adminValidationRules } from "../../pages/superadmin/validation";
import { formatPayload } from "../../utils";

export const AddEditAdminForm = ({ adminId = null, companyId = null }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [adminDetails, setAdminDetails] = useState({
    username: undefined,
    firstName: undefined,
    lastName: undefined,
    companyId: companyId,
    password: undefined,
    primaryPhone: undefined,
    primaryEmail: undefined,
    primaryAddress: {
      locality: undefined,
      state: undefined,
      pinCode: undefined,
      country: undefined,
    },
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (adminId) fetchData(adminId);
  }, [adminId]);

  useEffect(() => {
    fetchCompany(companyId);
  }, [companyId]);

  const fetchData = async (id) => {
    try {
      const { data: admin } = await AdminService.getUserDetails(id);
      setAdminDetails({
        username: admin?.username || undefined,
        firstName: admin?.firstName || undefined,
        lastName: admin?.lastName || undefined,
        primaryPhone: admin?.primaryPhone || undefined,
        primaryEmail: admin?.primaryEmail || undefined,
        primaryAddress: {
          locality: admin?.primaryAddress?.locality || undefined,
          state: admin?.primaryAddress?.state || undefined,
          pinCode: admin?.primaryAddress?.pinCode || undefined,
          country: admin?.primaryAddress?.country || undefined,
        },
      });
      setSelectedCompany(admin?.company || null);
    } catch (error) {
      toast.error("Failed to fetch admin details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const fetchCompany = async (id) => {
    try {
      const { data } = await CompanyService.getCompanyDetails(id);
      setSelectedCompany(data);
    } catch (error) {
      console.error("🚀 ~ fetchCompany ~ error:", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    if (["locality", "state", "pinCode", "country"].includes(name)) {
      setAdminDetails({
        ...adminDetails,
        primaryAddress: { ...adminDetails.primaryAddress, [name]: value },
      });
    } else {
      setAdminDetails({ ...adminDetails, [name]: value });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleCancelClick = () => {
    setErrors({});
    setAdminDetails({
      username: undefined,
      firstName: undefined,
      lastName: undefined,
      companyId: undefined,
      password: undefined,
      primaryPhone: undefined,
      primaryEmail: undefined,
      primaryAddress: {
        locality: undefined,
        state: undefined,
        pinCode: undefined,
        country: undefined,
      },
    });
    navigate(-1);
  };

  const formatAdminPayload = (details) => {
    const formatted = { ...details };
    const payload = formatPayload(formatted);

    if (payload.primaryAddress) {
      const address = payload.primaryAddress;
      const keys = ["locality", "state", "pinCode", "country"];
      const allEmpty = keys.every((key) => !address[key] || address[key].toString().trim() === "");

      if (allEmpty) {
        // all empty → set empty string for each key
        keys.forEach((key) => {
          address[key] = "";
        });
      } else {
        // some have value → fill missing with empty string
        keys.forEach((key) => {
          if (!address[key] || address[key].toString().trim() === "") {
            address[key] = "";
          }
        });
      }
    }

    return payload;
  };

  const handleSaveAdmin = async () => {
    setErrors({});
    const formattedData = formatAdminPayload(adminDetails);
    const validationErrors = adminValidationRules(formattedData, !!adminId);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      if (adminId) await AdminService.updateUser(adminId, formattedData);
      else await AdminService.createUser({ ...formattedData, type: "ADMIN" });
      toast.success(`Admin ${adminId ? "updated" : "added"} successfully`);
      handleCancelClick();
    } catch (error) {
      toast.error(`Error ${adminId ? "updating" : "adding"} admin`);
      console.error("🚀 ~ handleSaveAdmin ~ error:", error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <CardLayout>
      <Box className="fl gap-2">
        <InputField
          required
          label="Username"
          name="username"
          value={adminDetails.username}
          onChange={updateDetailsState}
          error={!!errors.username}
          helperText={errors.username}
          style={{ width: "100%" }}
        />
        {!adminId && (
          <InputField
            required
            showPassword={showPassword}
            togglePasswordVisibility={togglePasswordVisibility}
            label="Password"
            name="password"
            type="password"
            value={adminDetails.password}
            onChange={updateDetailsState}
            error={!!errors.password}
            helperText={errors.password}
            autoComplete="new-password"
            style={{ width: "100%" }}
          />
        )}
        <InputField
          required
          label="First Name"
          name="firstName"
          value={adminDetails.firstName}
          onChange={updateDetailsState}
          error={!!errors.firstName}
          helperText={errors.firstName}
          style={{ width: "100%" }}
        />
        <InputField
          required
          label="Last Name"
          name="lastName"
          value={adminDetails.lastName}
          onChange={updateDetailsState}
          error={!!errors.lastName}
          helperText={errors.lastName}
          style={{ width: "100%" }}
        />
      </Box>
      <Box className="fl gap-2">
        <SelectField
          label="Company"
          value={selectedCompany}
          getData={CompanyService.getCompanyList}
          getDataStaticFilters={{ active: true, sortBy: "name", sortOrder: "asc" }}
          disabled={true}
          required
          error={!!errors.companyId}
          helperText={errors.companyId}
          style={{ width: "100%" }}
        />
        <InputField
          required
          type="tel"
          label="Mobile"
          name="primaryPhone"
          value={adminDetails.primaryPhone}
          onChange={updateDetailsState}
          error={!!errors.primaryPhone}
          helperText={errors.primaryPhone}
          style={{ width: "100%" }}
        />
        <InputField
          required
          type="email"
          label="Email"
          name="primaryEmail"
          value={adminDetails.primaryEmail}
          onChange={updateDetailsState}
          error={!!errors.primaryEmail}
          helperText={errors.primaryEmail}
          style={{ width: "100%" }}
        />
      </Box>
      <Box className="fl gap-2">
        <InputField
          label="Locality"
          name="locality"
          value={adminDetails.primaryAddress.locality}
          onChange={updateDetailsState}
          style={{ width: "100%" }}
        />
        <InputField
          label="State"
          name="state"
          value={adminDetails.primaryAddress.state}
          onChange={updateDetailsState}
          style={{ width: "100%" }}
        />
        <InputField
          type="number"
          label="Pin code"
          name="pinCode"
          value={adminDetails.primaryAddress.pinCode}
          onChange={updateDetailsState}
          error={!!errors?.primaryAddress?.pinCode}
          helperText={errors?.primaryAddress?.pinCode}
          style={{ width: "100%" }}
        />
        <InputField
          label="Country"
          name="country"
          value={adminDetails.primaryAddress.country}
          onChange={updateDetailsState}
          style={{ width: "100%" }}
        />
      </Box>
      <Box className={"d-flex justify-content-end gap-1"}>
        <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleCancelClick} />
        <ActionButton buttonText="Submit" color="primary" disabled={loading} onButtonClick={handleSaveAdmin} />
      </Box>
    </CardLayout>
  );
};
